import React, { Component, useState, useEffect, useRef, useCallback } from "react";
import Box from '@mui/material/Box';
import { Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HighlightIcon from '@mui/icons-material/Highlight';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import TransformIcon from '@mui/icons-material/Transform';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import CommentIcon from '@mui/icons-material/Comment';
import { DateTime, FixedOffsetZone } from "luxon";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Quill from 'quill';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { Editor } from 'primereact/editor';
import { SimpleUploadFileDialog } from "../workflow/workflow-select-draft";
import { openPdfDocument }  from "../webviewer";
import "quill/dist/quill.core.css";
import "./document-annotations-quill-editor.css";

import { UserInfo, netGet, netPost, registerInitHook } from "../network";

const DATE_FORMAT = 'dd LLL yyyy HH:mm:ss';
const REVNOTE_FORMAT = 'yyMMddHHmmssSSSZZZ';

const renderChildren = (root) => {
    if ( root.hasChildNodes() ) {
        const children = [];
        for(const node of root.childNodes ) {
            children.push(node);
        }
        return children.map(n => renderRevisionNote(n));
    }
};

const renderRevisionNote = (root) => {
    switch ( root.nodeType ) {
        case Node.ELEMENT_NODE:
            switch (root.localName ) {
                case 'revisionNote':
                case 'editorialNote':
                case 'approvalNote':
                case 'interpretationNote':
                    return (
                        <React.Fragment>
                            {renderChildren(root)}
                        </React.Fragment>
                    );
                case 'para':
                    return (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {renderChildren(root)}
                        </Typography>
                    )
                case 'itemizedList':
                    return (
                        <Typography component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
                            {renderChildren(root)}
                        </Typography>
                    )
                case 'orderedList':
                    return (
                        <Typography component="ol" sx={{ listStyleType: 'decimal', pl: 4 }}>
                            {renderChildren(root)}
                        </Typography>
                    );
                case 'item':
                    return (
                        <Typography component="li">
                            {renderChildren(root)}
                        </Typography>
                    )
                default:
                    return (
                        <React.Fragment>
                            {renderChildren(root)}
                        </React.Fragment>
                    );
            }
            break;
        case Node.TEXT_NODE:
            return root.textContent;
    }
};

const convertToQuill = (root, ops) => {
    switch (root.nodeType) {
        case Node.ELEMENT_NODE:
            switch (root.localName) {
                case 'para':
                    ops.push({
                        insert: root.textContent + '\n',
                    });
                    break;
                case 'itemizedList':
                    for(const child of root.children) {
                        ops.push({
                            insert: child.textContent + '\n',
                            attributes: {'list': 'bullet'},
                        });
                    }
                    break;
                case 'orderedList':
                    for(const child of root.children) {
                        ops.push({
                            insert: child.textContent + '\n',
                            attributes: {'list': 'ordered'},
                        });
                    }
                    break;
                default:
                    for(const child of root.children) {
                        convertToQuill(child, ops);
                    }
                    break;
            }
    }
};

var quillLoaded = false;


function EditRevisionNoteDialog(props) {
    const {
        title = 'Add Revision Note',
        type,
        open,
        edit = false,
        note,
        onSave,
        onCancel,
        ...other
    } = props;

    // const [content, setContent] = useState('');
    const [okEnabled, setOkEnabled] = useState(false);
    const [text, setText] = useState('');
    const [loaded, setLoaded] = useState(false);

    const quillRef = useRef(null);
    
    useEffect(() => {
        // setContent('');
        setText('');
        
        if ( open ) {
            setOkEnabled(false);
        }
    }, [open, edit, note]);

    const onQuillLoaded = (q) => {
        if (open && edit && note) {
            // console.log('Editing revision note3');
            const quill = quillRef.current?.getQuill();
            const ops = [];
            if (note.content) {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(note.content, "text/xml");
                convertToQuill(xmlDoc.documentElement, ops);
            }
            // console.log('Editing ops: ' + JSON.stringify(ops));
            if (quill) {
                quill.setContents(ops);
                setTimeout(() => {
                    setOkEnabled(false);
                }, 400);
            }
        }
    };

    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const getRootTag = () => {
        switch ( type ) {
            case 'Highlight':
                return 'revisionNote';
            case 'Editorial':
                return 'editorialNote';
            case 'Approval':
                return 'approvalNote';
            case 'Interpretation':
                return 'interpretationNote';
        }
        return 'revisionNote';
    }

    const convert = (root) => {
        let r = '';
        let tag = root.localName;
        switch (root.nodeType ) {
            case Node.ELEMENT_NODE:
                switch ( root.localName ) {
                    case 'html':
                        tag = getRootTag();
                        break;
                    case 'p':
                        tag = 'para';
                        break;
                    case 'ol':
                        tag = 'orderedList';
                        break;
                    case 'ul':
                        tag = 'itemizedList';
                        break;
                    case 'li':
                        tag = 'item';
                        break;
                }
                r += '<' + tag;
                if ( root.id ) {
                    r += ' id="' + root.id + '"';
                }
                r += '>';
                if ( tag === 'item' ) {
                    r += '<para>';
                }
                root.childNodes.forEach ( n => {
                    r += convert(n);
                })
                if ( tag === 'item' ) {
                    r += '</para>';
                }
                r += '</' + tag + '>';
                break;
            case Node.TEXT_NODE:
                r += root.textContent;
                break;
        }
        return r;
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            const quill = quillRef.current?.getQuill();
            if ( quill ) {
                const qcontent = quill.getContents();
                // console.log('Quill Contents: ' + JSON.stringify(qcontent));
                // convert first to xhtml
                console.log('Saving ' + JSON.stringify(qcontent.ops));
                const converter = new QuillDeltaToHtmlConverter(qcontent.ops, {multiLineParagraph: false});
                const html = converter.convert();
                console.log('HTML: ' + html);
                // then convert to our revision note schema
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString('<html>' + html + '</html>', "text/xml");
                const xml = convert(xmlDoc.documentElement);
                console.log('Quill XML: ' + xml);
                onSave({
                    type: type,
                    content: xml,
                    note: note,
                    edit: edit,
                });
            }
        }
    }

    /*
    const handleContentChange = (event) => {
        const v = event.target.value;
        setContent(event.target.value);
        setOkEnabled(v && v !== '');
    }
    */

    const handleSelectionChange = (event) => {
        // getting selection events from InsertVariableDialog!?
        /*
        if (event?.range) {
            setSelectionRange(event.range);
        }
        */
    };

    const handleTextChange = (e) => {
        setOkEnabled(true);
        setText(e.htmlValue);
    }

    const renderHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-list" value="bullet" aria-label="bullet list"></button>
                <button className="ql-list" value="ordered" aria-label="ordered list"></button>
            </span>
        );
    };

    const header = renderHeader();

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 600,
                        maxWidth: 600,
                        maxHeight: 550,
                        minHeight: 400,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent>
                {/* <TextField label="Content" value={content} onChange={handleContentChange} fullWidth multiline size="small" sx={{marginTop: 2}} rows="8" /> */}
                <Box sx={{ paddingTop: '2ex' }}>
                    <Editor headerTemplate={header} value={text} onTextChange={handleTextChange} style={{ height: '220px', fontSize: '16px' }} 
                        onSelectionChange={handleSelectionChange} ref={quillRef} onLoad={onQuillLoaded} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} >{edit ? 'Save' : 'Add'}</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}

function DeleteRevisionNote(props) {
    const {
        title = 'Delete Revision Note',
        index,
        note,
        open,
        onSave,
        onCancel,
        ...other
    } = props;

    const [okEnabled, setOkEnabled] = useState(true);

    useEffect(() => {
    }, [open]);

    const onCancelClick = () => {
        onClose();
    };

    const onClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const onOKClick = () => {
        if (typeof onSave === 'function') {
            onSave({
                note: note,
                index: index,
            });
        }
    }

    const getRevisionNoteLabel = () => {
        /*
        switch ( note?.type ) {
            case 'Highlight':
                return 'Revision Highlight';
            case 'Editorial':
                return 'Editorial Note';
            case 'Interpretation':
                return 'Interpretation Note';
            case 'Approval':
                return 'Approval Note';
            case 'Collateral':
                return 'Collateral Attachment';
            default:
                return 'Revision Note';
        }
        */
        // return getAnnotSummary(note) + "?";
        return getAnnotBody(note);
    };

    const getAnnotBody = (annot) => {
        if ( annot?.type === 'Collateral' ) {
            return (
                <React.Fragment>
                    <Box sx={{display: 'flex'}}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', mr: 1 }}>File: </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{annot.collateral?.displayName}</Typography>
                    </Box>
                    {annot.collateral?.description &&
                        <Box sx={{display: 'flex'}}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', mr: 1}}>Description: </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{annot.collateral?.description}</Typography>
                        </Box>}
                </React.Fragment>
            );
        }
        if ( annot?.content ) {
            if ( annot.content.length > 1 ) {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(annot.content, "text/xml");
                return renderRevisionNote(xmlDoc.documentElement);
            }
        }
        return '';
    };

    return (
        <Dialog
            maxWidth={'900px'}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 600,
                        maxWidth: 600,
                        maxHeight: 450,
                        minHeight: 350,
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
            <DialogContent sx={{ overflowY: 'auto'}}>
                <Box sx={{pl: 3}}>
                    {getRevisionNoteLabel()}
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1}}>
                <Button variant="outlined" onClick={() => onOKClick()} sx={{ minWidth: '7em' }} disabled={!okEnabled} color="error" >Delete</Button>
                <Button variant="outlined" onClick={onCancelClick} sx={{ minWidth: '7em', marginLeft: 2 }}>Cancel</Button>
            </DialogActions>
        </Dialog>

    );

}

function AnnotationsPanel(props) {
    const {
        document,
        id,
        selectedNode,
        selectedAnnot, // metadata instance
        setDocAnnot,
        open,
        onSave,
        onCancel,
        onClose,
        visible,
        ...other
    } = props;

    const [node, setNode] = useState(null);
    const [annot, setAnnot] = useState(null);
    const [expanded, setExpanded] = useState([]);
    const [addTitle, setAddTitle] = useState('Add Revision Highlight');
    const [addType, setAddType] = useState('Highlight');
    const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
    const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
    const [editNote, setEditNote] = useState(null);
    const [editNoteIndex, setEditNoteIndex] = useState(-1);
    const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
    const [deleteNote, setDeleteNote] = useState(null);
    const [deleteIndex, setDeleteIndex] = useState(-1);
    const [deleteTitle, setDeleteTitle] = useState('Delete Revision Note');
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

    useEffect( () => {
        setNode(selectedNode);
    },[selectedNode]);

    useEffect( () => {
        setAnnot(selectedAnnot);
        console.log('Selected Annotation: ' + JSON.stringify(selectedAnnot));
        if ( Array.isArray(selectedAnnot?.annotations) ) {
            setExpanded(new Array(selectedAnnot.annotations.length).fill(false));
        }
    },[selectedAnnot]);

    const handleDrawerClose = () => {
        if ( typeof onClose === 'function') {
            onClose();
        }
    };

    const handleAddHighlight = () => {
        setAddTitle('Add Revision Note');
        setAddType('Highlight');
        setAddNoteDialogOpen(true);
    };

    const handleAddEditorial = () => {
        setAddTitle('Add Editorial Note');
        setAddType('Editorial');
        setAddNoteDialogOpen(true);
    };

    const handleAddApproval = () => {
        setAddTitle('Add Approval Note');
        setAddType('Approval');
        setAddNoteDialogOpen(true);
    };

    const handleAddInterpretation = () => {
        setAddTitle('Add Interpretation Note');
        setAddType('Interpretation');
        setAddNoteDialogOpen(true);
    };

    const handleCreateNoteSave = (spec) => {
        setAddNoteDialogOpen(false);
        if ( typeof setDocAnnot === 'function') {
            const nannot = selectedAnnot ? {...selectedAnnot} : { 
                id: selectedNode?.attributes?.id,
                annotations: [],
            };
            nannot.annotations.push({
                duId: id,
                type: spec.type,
                user: UserInfo.info.name,
                modificationDate: DateTime.now().toFormat(REVNOTE_FORMAT),
                readOnly: false,
                content: spec.content, 
                isPersistent: false,
                isEditionClosed: false,
            });
            setDocAnnot(nannot);
        }
    };

    const handleEditNote = ( note, index) => {
        setAddType(note.type);
        setEditNote(note);
        setEditNoteIndex(index);
        setAddTitle('Edit ' + getAnnotTitle(note));
        setEditNoteDialogOpen(true);
    };

    const handleEditNoteSave = (spec) => {
        setEditNoteDialogOpen(false);
        if ( typeof setDocAnnot === 'function' ) {
            const nannot = selectedAnnot ? {...selectedAnnot} : { 
                id: selectedNode?.attributes?.id,
                annotations: [],
            };
            if ( editNoteIndex >= 0 && editNoteIndex < nannot.annotations?.length ) {
                const note = nannot.annotations[editNoteIndex];
                if ( note ) {
                    note.content = spec.content;
                }
                setDocAnnot(nannot);
            }
        }
    };

    const handleDeleteNote = (note, index) => {
        setDeleteTitle('Delete ' + getAnnotTitle(note));
        setDeleteNote(note);
        setDeleteIndex(index);
        setDeleteNoteDialogOpen(true);
    };

    const handleDeleteNoteSave = (spec) => {
        setDeleteNoteDialogOpen(false);
        if ( typeof setDocAnnot === 'function') {
            const nannot = selectedAnnot ? {...selectedAnnot} : { 
                id: selectedNode?.attributes?.id,
                annotations: [],
            };
            if ( spec.index < nannot.annotations.length && spec.index >= 0 ) {
                nannot.annotations.splice(spec.index, 1);
                setDocAnnot(nannot);
            }
        }
    };

    const handleAddCollateral = () => {
        setUploadDialogOpen(true);
    };

    const saveUploadCollateral = (data) => {
        setUploadDialogOpen(false);
        console.log('Uploaded files: ' + JSON.stringify(data));
        if ( Array.isArray(data) ) {
            data.forEach( file => {
                if (typeof setDocAnnot === 'function') {
                    const nannot = selectedAnnot ? { ...selectedAnnot } : {
                        id: selectedNode?.attributes?.id,
                        annotations: [],
                    };
                    nannot.annotations.push({
                        duId: id,
                        type: 'Collateral',
                        user: UserInfo.info.name,
                        modificationDate: DateTime.now().toFormat(REVNOTE_FORMAT),
                        readOnly: false,
                        isPersistent: false,
                        isEditionClosed: false,
                        /*
                        originalFilePath: file.path,
                        displayName: file.label,
                        description: file.description,
                        */
                        collateral: {
                            displayName: file.label,
                            description: file.description,
                            duId: id,
                            collateralFile: {
                                originalFilePath: file.path,
                            }
                        }
                    });
                    setDocAnnot(nannot);
                }
            });
        }
    };

    const ExpandMore = styled((props) => {
        const { expand, ...other } = props;
        return <IconButton {...other} />;
    })(({ theme }) => ({
        marginLeft: 'auto',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
        variants: [
            {
                props: ({ expand }) => !expand,
                style: {
                    transform: 'rotate(0deg)',
                },
            },
            {
                props: ({ expand }) => !!expand,
                style: {
                    transform: 'rotate(180deg)',
                },
            },
        ],
    }));

    const getAnnotTitle = (annot) => {
        switch ( annot?.type ) {
            case 'Highlight':
                return 'Revision Note';
            case 'Editorial':
                return 'Editorial Note';
            case 'Approval':
                return 'Approval Note';
            case 'Interpretation':
                return 'Interpretation Note';
            case 'Collateral':
                return 'Collateral Attachment';
        }
        return '';
    };

    const getAnnotSubheader = (annot) => {
        let sh = '';
        if ( annot.user ) {
            sh = annot.user;
            if ( annot.modificationDate ) {
                sh = <React.Fragment><Typography fontSize="92%" sx={{lineHeight: 1}}>{sh}</Typography><Typography fontSize="92%" sx={{lineHeight: 1}}>{DateTime.fromFormat(annot.modificationDate, REVNOTE_FORMAT).toFormat(DATE_FORMAT)}</Typography></React.Fragment> ;
            } 
        } else if ( annot.modificationDate ) {
            sh = "DateTime.fromFormat(annot.modificationDate, REVNOTE_FORMAT).toFormat(DATE_FORMAT)";
        }
        return sh;
    };

    const getAnnotSummary = (annot) => {
        if ( annot.content ) {
            if ( annot.content.length > 1 ) {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(annot.content, "text/xml");
                const paras = xmlDoc.getElementsByTagName('para');
                if ( paras?.length > 0 ) {
                    let text = paras[0].textContent;
                    /*
                    if ( text.length > 32 ) {
                        return text.substring(0, 30) + "...";
                    }
                    */
                    if ( paras.length > 1 ) {
                        text += '...';
                    }
                    if ( paras[0].parentElement?.localName === 'item' ) {
                        return (
                            paras[0].parentElement.parentElement?.localName === 'orderedList' ?
                                <Typography component="ol" sx={{ listStyleType: 'decimal', pl: 4 }}>
                                    <Typography component="li">
                                        {text}
                                    </Typography>
                                </Typography> :
                                <Typography component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
                                    <Typography component="li">
                                        {text}
                                    </Typography>
                                </Typography>
                        );
                    }
                    return text;
                }
            }
        }
        return '';
    };

    const hasManyParas = (annot) => {
        if ( annot.type === 'Collateral' ) {
            return false;
        }
        if ( annot.content ) {
            if ( annot.content.length > 1 ) {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(annot.content, "text/xml");
                const paras = xmlDoc.getElementsByTagName('para');
                if ( paras?.length > 1 ) {
                    return true;
                }
            }
        }
        return false;
    };

    const getAnnotBody = (annot) => {
        if ( annot.type === 'Collateral' ) {
            return (
                <React.Fragment>
                    <Box sx={{display: 'flex'}}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', mr: 1 }}>File: </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{annot.collateral?.displayName}</Typography>
                    </Box>
                    {annot.collateral?.description &&
                        <Box sx={{display: 'flex'}}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', mr: 1}}>Description: </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{annot.collateral?.description}</Typography>
                        </Box>}
                </React.Fragment>
            );
        }
        if ( annot.content ) {
            if ( annot.content.length > 1 ) {
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(annot.content, "text/xml");
                /*
                const paras = xmlDoc.getElementsByTagName('para');
                const pp = [];
                for(let i=0; i<paras.length; i++) {
                    pp.push(paras[i]);
                }
                return pp.map( p => 
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.textContent}</Typography>
                );
                */
                return renderRevisionNote(xmlDoc.documentElement);
            }
        }
        return '';
    };

    const handleViewCollateral = (annot, index) => {
        if ( annot?.collateral?.collateralFile?.originalFilePath ) {
            openPdfDocument(annot.collateral.collateralFile.originalFilePath);
        }
    };

    const handleExpandClick = (annot, index) => {
        const ex = [...expanded];
        const v = ex[index];
        ex.fill(false);
        ex[index] = !v;
        setExpanded(ex);
    };

    return (
        <Box sx={{ position: 'absolute', display: visible ? 'flex' : 'none', width: '100%', height: '100%', flexDirection: 'column' }}>
            <Box sx={{
                display: 'flex', justifyContent: 'flex-start',
                padding: 1, paddingLeft: 0, paddingRight: 2, paddingBottom: 0,
                alignItems: 'center',
                borderTopWidth: 'var(--xui-border-width)', borderTopColor: 'var(--xui-border-color)', borderTopStyle: 'solid',
            }}>
                <Tooltip title="Close">
                    <IconButton onClick={handleDrawerClose} size="small" sx={{mr: 1, ml: 1}}>
                        <CloseIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
                <Typography sx={{ fontWeight: 'bold' }}>Annotations</Typography>
            </Box>
            <Box sx={{
                marginTop: '2px', padding: 1,
                borderTopWidth: 'var(--xui-border-width)', borderTopColor: 'var(--xui-border-color)', borderTopStyle: 'solid', borderTopLeftRadius: '3px'
            }}>
                <Box sx={{
                    marginTop: 2, 
                    width: '100%',
                    fontSize: '92%'
                }}>
                    <Box sx={{ display: 'flex', marginRight: 1, alignItems: 'baseline', fontSize: 'inherit' }}>
                        <Typography sx={{ fontWeight: 'bold', width: '70px', fontSize: 'inherit' }}>Element:</Typography>
                        <TextField defaultValue={node?.tag} variant="standard" slotProps={{
                            input: {
                                readOnly: true,
                                sx: { fontSize: 'inherit', width: '180px' }
                            },
                        }} />
                    </Box>
                    {/* 
                    <Box sx={{ display: 'flex', marginTop: 2, alignItems: 'baseline', fontSize: 'inherit' }}>
                        <Typography sx={{ fontWeight: 'bold', width: '70px', fontSize: 'inherit' }}>ID:</Typography>
                        <TextField readOnly defaultValue={node?.attributes?.id} variant="standard" slotProps={{
                            input: {
                                readOnly: true,
                                sx: { fontSize: '90%', width: '180px' }
                            },
                        }} />
                    </Box>
                    */}
                </Box>
            </Box>
            <Box sx={{flexGrow: 1, overflowY: 'auto'}} >
                {Array.isArray(selectedAnnot?.annotations) && selectedAnnot.annotations.map( (annot,index) => 
                    <Card sx={{ margin: 1 }} variant="outlined">
                        <CardHeader
                            sx={{paddingTop: '8px', paddingBottom: '8px', paddingRight: '2px'}}
                            avatar={
                                <Avatar aria-label="Revision Note" fontSize="92%">
                                    { annot?.type === 'Highlight' && <CommentIcon/> }
                                    { annot?.type === 'Editorial' && <EditSquareIcon/> }
                                    { annot?.type === 'Approval' && <ThumbUpIcon/> }
                                    { annot?.type === 'Interpretation' && <HighlightIcon/> }
                                    { annot?.type === 'Collateral' && <AttachFileIcon/> }
                                </Avatar>
                            }
                            
                            title={getAnnotTitle(annot)}
                            subheader={getAnnotSubheader(annot)}
                        />
                        <CardContent>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {expanded[index] || annot?.type === 'Collateral' ? getAnnotBody(annot) : getAnnotSummary(annot)}
                            </Typography>
                        </CardContent>
                        <CardActions disableSpacing>
                            {annot?.type !== 'Collateral' ?
                                <Tooltip title="Edit">
                                    <IconButton aria-label="edit revision note" size="small" onClick={() => handleEditNote(annot, index)} >
                                        <EditIcon color="success" fontSize="inherit" />
                                    </IconButton>
                                </Tooltip> :
                                <Tooltip title="View">
                                    <IconButton aria-label="view collateral attachment" size="small" onClick={() => handleViewCollateral(annot, index)} >
                                        <PreviewIcon color="success" fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            }
                            <Tooltip title="Delete">
                                <IconButton aria-label="delete revision note" size="small" onClick={() => handleDeleteNote(annot,index)} >
                                    <DeleteIcon color="error" fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <ExpandMore
                                disabled={!hasManyParas(annot)}
                                expand={expanded[index]}
                                onClick={() => handleExpandClick(annot,index)}
                                aria-expanded={expanded[index]}
                                aria-label="show more"
                                sx={{visibility: hasManyParas(annot) ? 'visible' : 'hidden'}}
                            >
                                <ExpandMoreIcon />
                            </ExpandMore>
                        </CardActions>
                        {/* 
                        <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
                            <CardContent>
                                <Typography sx={{ marginBottom: 2 }}>Method:</Typography>
                                <Typography sx={{ marginBottom: 2 }}>
                                    Heat 1/2 cup of the broth in a pot until simmering, add saffron and set
                                    aside for 10 minutes.
                                </Typography>
                                <Typography sx={{ marginBottom: 2 }}>
                                    Heat oil in a (14- to 16-inch) paella pan or a large, deep skillet over
                                    medium-high heat. Add chicken, shrimp and chorizo, and cook, stirring
                                    occasionally until lightly browned, 6 to 8 minutes. Transfer shrimp to a
                                    large plate and set aside, leaving chicken and chorizo in the pan. Add
                                    pimentón, bay leaves, garlic, tomatoes, onion, salt and pepper, and cook,
                                    stirring often until thickened and fragrant, about 10 minutes. Add
                                    saffron broth and remaining 4 1/2 cups chicken broth; bring to a boil.
                                </Typography>
                                <Typography sx={{ marginBottom: 2 }}>
                                    Add rice and stir very gently to distribute. Top with artichokes and
                                    peppers, and cook without stirring, until most of the liquid is absorbed,
                                    15 to 18 minutes. Reduce heat to medium-low, add reserved shrimp and
                                    mussels, tucking them down into the rice, and cook again without
                                    stirring, until mussels have opened and rice is just tender, 5 to 7
                                    minutes more. (Discard any mussels that don&apos;t open.)
                                </Typography>
                                <Typography>
                                    Set aside off of the heat to let rest for 10 minutes, and then serve.
                                </Typography>
                            </CardContent>
                        </Collapse>
                        */}
                    </Card> )
                }
            </Box>
            <Box sx={{paddingBottom: 1}}>
                <Tooltip title="Add Revision Note">
                    <IconButton onClick={handleAddHighlight} size="small" sx={{marginLeft: 2}}>
                        <CommentIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Add Approval Note">
                    <IconButton onClick={handleAddApproval} size="small" sx={{marginLeft: 2}}>
                        <ThumbUpIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Add Editorial Note">
                    <IconButton onClick={handleAddEditorial} size="small" sx={{marginLeft: 2}}>
                        <EditSquareIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Add Interpretation Note">
                    <IconButton onClick={handleAddInterpretation} size="small" sx={{marginLeft: 2}}>
                        <HighlightIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Add Collateral Attachment">
                    <IconButton onClick={handleAddCollateral} size="small" sx={{marginLeft: 2}}>
                        <AttachFileIcon fontSize="inherit"/>
                    </IconButton>
                </Tooltip>
            </Box>
            <EditRevisionNoteDialog title={addTitle} type={addType} open={addNoteDialogOpen} onCancel={() => setAddNoteDialogOpen(false)} onSave={handleCreateNoteSave} />
            <EditRevisionNoteDialog title={addTitle} type={addType} open={editNoteDialogOpen} onCancel={() => setEditNoteDialogOpen(false)} onSave={handleEditNoteSave} 
                edit={true} note={editNote} />
            <DeleteRevisionNote title={deleteTitle} open={deleteNoteDialogOpen} onCancel={() => setDeleteNoteDialogOpen(false)} onSave={handleDeleteNoteSave} 
                note={deleteNote} index={deleteIndex} />
            <SimpleUploadFileDialog open={uploadDialogOpen} onCancel={() => setUploadDialogOpen(false)} 
                title="Upload Collateral Attachments"
                acceptLabel="Drag & drop files here, or click/tap Select to browse the local filesystem."
                onSave={saveUploadCollateral} />
        </Box>
    );
}

export { AnnotationsPanel };